import { memo, useCallback, type MouseEvent, type ReactNode } from "react";
import { isInteractive } from "@funnel-ui/primitives/isInteractive";
import { DynamicIcon } from "@funnel-ui/primitives/DynamicIcon";
import { StepHeading } from "@funnel-ui/primitives/StepHeading";
import { ResponsiveAsset } from "@funnel-ui/primitives/ResponsiveAsset";

/** Feature-detect WebM video playback (vp9 or vp8 codec). */
export function canPlayWebM(): boolean {
  if (typeof document === "undefined") return false;
  const video = document.createElement("video");
  return (
    video.canPlayType('video/webm; codecs="vp9"') !== "" ||
    video.canPlayType('video/webm; codecs="vp8"') !== ""
  );
}

export type InfoBullet = {
  icon?: string;
  text: string;
};

/** Controls vertical positioning of text content in full-bleed layout. */
export type TextPosition = "top" | "bottom";

type InfoScreenProps = {
  headline: string;
  description?: string;
  /** Small uppercase label rendered above the headline in accent color. */
  eyebrow?: string;
  bullets?: InfoBullet[];
  hero?: ReactNode;
  heroImageUrl?: string;
  /** Full-bleed background image with vignette gradient. Mutually exclusive with heroImageUrl. */
  backgroundImageUrl?: string;
  /** WebM video URL for full-bleed background. Uses backgroundImageUrl as poster. */
  backgroundVideoUrl?: string;
  /** Small text rendered below the CTA area (rendered in the global footer). */
  footnote?: string;
  /** Vertical positioning of text overlay in full-bleed layout. Defaults to "top". */
  textPosition?: TextPosition;
  /** Called when the user taps/clicks anywhere on the step (outside interactive elements). */
  onPress?: () => void;
  className?: string;
  testId?: string;
};

const FullBleedLayout = ({
  headline,
  description,
  eyebrow,
  bullets,
  backgroundImageUrl,
  backgroundVideoUrl,
  footnote: _footnote,
  textPosition = "top",
  className,
  testId,
}: Pick<
  InfoScreenProps,
  | "headline"
  | "description"
  | "eyebrow"
  | "bullets"
  | "backgroundImageUrl"
  | "backgroundVideoUrl"
  | "footnote"
  | "textPosition"
  | "className"
  | "testId"
>) => (
  <section
    data-testid={testId}
    className={`relative -mx-5 -mt-5 -mb-6 flex flex-1 flex-col ${className ?? ""}`}
  >
    {backgroundVideoUrl && canPlayWebM() ? (
      <video
        className="absolute inset-0 h-full w-full object-cover"
        muted
        autoPlay
        loop
        playsInline
        preload="none"
        poster={backgroundImageUrl}
      >
        <source src={backgroundVideoUrl} type="video/webm" />
      </video>
    ) : (
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url("${backgroundImageUrl}")` }}
      />
    )}
    <div
      className="absolute inset-0"
      style={{
        background:
          textPosition === "bottom"
            ? "linear-gradient(to bottom, hsl(var(--brand-bg) / 0.15) 0%, hsl(var(--brand-bg) / 0.25) 35%, hsl(var(--brand-bg) / 0.65) 60%, hsl(var(--brand-bg) / 0.85) 80%, hsl(var(--brand-bg)) 100%)"
            : "linear-gradient(to top, hsl(var(--brand-bg)) 0%, hsl(var(--brand-bg) / 0.55) 40%, hsl(var(--brand-bg) / 0.15) 65%, hsl(var(--brand-bg) / 0.4) 85%, hsl(var(--brand-bg) / 0.7) 100%)",
      }}
    />
    <div
      className={`relative z-10 flex flex-1 flex-col px-5 pt-6 pb-6 ${
        textPosition === "bottom" ? "justify-end" : "justify-between"
      }`}
    >
      <div className="flex flex-col items-center gap-4">
        <StepHeading title={headline} description={description} eyebrow={eyebrow} align="center" />
        {bullets && bullets.length > 0 && (
          <ul className="flex w-full flex-col gap-3">
            {bullets.map((bullet, index) => (
              <li
                key={index}
                className="flex items-start gap-3 rounded-xl p-3"
                style={{ background: "hsl(var(--brand-bg-foreground) / 0.06)" }}
              >
                {bullet.icon && (
                  <span
                    className="mt-0.5 inline-flex shrink-0 items-center justify-center rounded-full p-1.5"
                    style={{
                      background: "hsl(var(--brand-primary) / 0.12)",
                      color: "hsl(var(--brand-primary))",
                    }}
                  >
                    <DynamicIcon icon={bullet.icon} size={16} />
                  </span>
                )}
                <span
                  className="flex-1 text-sm leading-relaxed"
                  style={{ color: "hsl(var(--brand-bg-foreground))" }}
                >
                  {bullet.text}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
      {/* footnote is rendered in the global footer (below the CTA button) */}
    </div>
  </section>
);

const InfoScreenImpl = ({
  headline,
  description,
  eyebrow,
  bullets,
  hero,
  heroImageUrl,
  backgroundImageUrl,
  backgroundVideoUrl,
  footnote,
  textPosition,
  onPress,
  className,
  testId,
}: InfoScreenProps) => {
  const handleClick = useCallback(
    (e: MouseEvent) => {
      if (!onPress || isInteractive(e.target)) return;
      onPress();
    },
    [onPress],
  );
  if (backgroundImageUrl) {
    return (
      <div
        onClick={handleClick}
        className={`flex flex-1 flex-col${onPress ? " cursor-pointer" : ""}`}
      >
        <FullBleedLayout
          headline={headline}
          description={description}
          eyebrow={eyebrow}
          bullets={bullets}
          backgroundImageUrl={backgroundImageUrl}
          backgroundVideoUrl={backgroundVideoUrl}
          footnote={footnote}
          textPosition={textPosition}
          className={className}
          testId={testId}
        />
      </div>
    );
  }

  return (
    <section
      data-testid={testId}
      className={`flex flex-col items-center gap-6 ${className ?? ""}${onPress ? " cursor-pointer" : ""}`}
      onClick={handleClick}
    >
      {hero ??
        (heroImageUrl && (
          <ResponsiveAsset
            src={heroImageUrl}
            alt=""
            loading="eager"
            widthHint={160}
            data-editable-path="heroImageUrl"
            className="h-40 w-40 rounded-full object-cover"
          />
        ))}
      <StepHeading title={headline} description={description} eyebrow={eyebrow} align="center" />
      {bullets && bullets.length > 0 && (
        <ul className="flex w-full flex-col gap-3">
          {bullets.map((bullet, index) => (
            <li
              key={index}
              className="flex items-start gap-3 rounded-xl p-3"
              style={{
                background: "hsl(var(--brand-bg-foreground) / 0.04)",
              }}
            >
              {bullet.icon && (
                <span
                  className="mt-0.5 inline-flex shrink-0 items-center justify-center rounded-full p-1.5"
                  style={{
                    background: "hsl(var(--brand-primary) / 0.12)",
                    color: "hsl(var(--brand-primary))",
                  }}
                >
                  <DynamicIcon icon={bullet.icon} size={16} />
                </span>
              )}
              <span
                className="flex-1 text-sm leading-relaxed"
                style={{ color: "hsl(var(--brand-bg-foreground))" }}
              >
                {bullet.text}
              </span>
            </li>
          ))}
        </ul>
      )}
      {/* footnote is rendered in the global footer (below the CTA button) */}
    </section>
  );
};

export const InfoScreen = memo(InfoScreenImpl);

export default InfoScreen;
